import React, { useState } from "react";
import { Play, ShieldCheck } from "lucide-react";
import { getConsent, saveConsent, loadTracking, trackEvent } from "@/lib/tracking";

// DSGVO-safe Consent-Gate for external embeds (YouTube, Vimeo, Instagram, TikTok, Facebook, Loom).
// Direct MP4 files are hosted by us → no consent required.
const NEEDS_CONSENT = new Set(["youtube", "vimeo", "instagram", "tiktok", "facebook", "loom"]);

const PROVIDER_LABEL = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  loom: "Loom",
};

const PLACEHOLDER_KEY = "tn-video-unlocked";

const readUnlocked = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(PLACEHOLDER_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

const persistUnlocked = (set) => {
  try {
    sessionStorage.setItem(PLACEHOLDER_KEY, JSON.stringify([...set]));
  } catch {
    /* noop */
  }
};

export const VideoEmbed = ({ video, className = "" }) => {
  const consent = getConsent();
  const provider = video.provider || "unknown";
  const needsConsent = NEEDS_CONSENT.has(provider);
  const hasGlobalConsent = !!consent?.external_media;

  const [unlocked, setUnlocked] = useState(() =>
    !needsConsent || hasGlobalConsent || readUnlocked().has(video.id)
  );

  const activateOnce = () => {
    const set = readUnlocked();
    set.add(video.id);
    persistUnlocked(set);
    trackEvent("video_consent_unlock", { video_id: video.id, provider });
    setUnlocked(true);
  };

  const acceptAllMedia = () => {
    const merged = {
      statistics: !!consent?.statistics,
      marketing: !!consent?.marketing,
      external_media: true,
    };
    saveConsent(merged);
    loadTracking();
    trackEvent("video_consent_all_media", { video_id: video.id, provider });
    setUnlocked(true);
  };

  if (!video.embed_url) {
    return null;
  }

  if (unlocked) {
    if (provider === "mp4") {
      return (
        <div className={`relative aspect-video rounded-2xl overflow-hidden bg-black ${className}`} data-testid={`video-embed-${video.id}`}>
          <video
            src={video.embed_url}
            controls
            preload="metadata"
            poster={video.thumbnail || undefined}
            className="w-full h-full object-cover"
            data-testid={`video-mp4-${video.id}`}
          />
        </div>
      );
    }
    return (
      <div className={`relative aspect-video rounded-2xl overflow-hidden bg-black border border-white/10 ${className}`} data-testid={`video-embed-${video.id}`}>
        <iframe
          src={video.embed_url}
          title={video.title || PROVIDER_LABEL[provider] || "Video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full"
          referrerPolicy="strict-origin-when-cross-origin"
          data-testid={`video-iframe-${video.id}`}
        />
      </div>
    );
  }

  // Consent-Gate placeholder
  const providerLabel = PROVIDER_LABEL[provider] || provider;
  return (
    <div
      className={`relative aspect-video rounded-2xl overflow-hidden border border-white/15 bg-[#3A4230] ${className}`}
      data-testid={`video-consent-gate-${video.id}`}
    >
      {video.thumbnail && (
        <img
          src={video.thumbnail}
          alt={video.title || providerLabel}
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          loading="lazy"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/40" />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-14 h-14 rounded-full bg-[#E8E5D0]/15 border border-[#E8E5D0]/40 flex items-center justify-center mb-3 backdrop-blur-sm">
          <ShieldCheck size={22} className="text-[#E8E5D0]" />
        </div>
        <div className="font-display text-lg sm:text-xl font-black tracking-tight text-[#F3F1E8] mb-1">
          Video von {providerLabel}
        </div>
        <p className="text-xs sm:text-[13px] text-[#C9C6B4] max-w-md leading-relaxed mb-4">
          Beim Laden dieses Videos werden Daten an {providerLabel} übertragen.
          Details in unserer{" "}
          <a href="/datenschutz" className="underline hover:text-[#E8E5D0]">
            Datenschutzerklärung
          </a>
          .
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={activateOnce}
            className="inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] text-xs sm:text-sm font-extrabold uppercase tracking-wider rounded-full px-5 py-2.5"
            data-testid={`video-activate-once-${video.id}`}
          >
            <Play size={14} fill="#3A4230" /> Video aktivieren
          </button>
          <button
            type="button"
            onClick={acceptAllMedia}
            className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/[0.08] text-[#F3F1E8] text-[11px] sm:text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2.5"
            data-testid={`video-activate-all-${video.id}`}
          >
            Alle Medien immer laden
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoEmbed;
